// components/ui/tooltip.tsx
"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "lib/utils";

export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

type TooltipContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
  arrow?: boolean;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
};

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      side = "top",
      sideOffset = 8,
      arrow = true,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={ref}
          side={side}
          sideOffset={sideOffset}
          className={cn(
            // IMPORTANT: do NOT use your `.tooltip` class here (it has opacity:0)
            "static z-50 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg",
            "bg-gray-900 text-white dark:bg-dark-700 dark:text-gray-200 border border-transparent dark:border-dark-500",
            // Visibility + transitions controlled by Radix data-state
            "opacity-0 pointer-events-none transition-opacity duration-200",
            "data-[state=open]:opacity-100 data-[state=open]:pointer-events-auto",
            // Side-aware entrance motion (optional, matches your animation vibe)
            "data-[side=bottom]:translate-y-1 data-[side=bottom]:data-[state=open]:translate-y-0",
            "data-[side=top]:-translate-y-1 data-[side=top]:data-[state=open]:translate-y-0",
            "data-[side=left]:-translate-x-1 data-[side=left]:data-[state=open]:translate-x-0",
            "data-[side=right]:translate-x-1 data-[side=right]:data-[state=open]:translate-x-0",
            className
          )}
          {...props}
        >
          {children}
          {arrow && (
            <TooltipPrimitive.Arrow
              width={12}
              height={6}
              className="fill-gray-900 dark:fill-dark-700"
            />
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    );
  }
);
TooltipContent.displayName = "TooltipContent";
